<template>
    <div>
        <!-- Search Mode -->
        <div v-if="mode === 'search'">
            <a v-if="showSearchButton" :disabled='disabled' href="#" :tabindex="tabindex" @click.prevent="findEquivalentDrugs">
                <b-icon icon="search" class="mr-1"></b-icon>
                <span>Search for Equivalent Drugs </span>
                <b-spinner v-if='isLoading' label="Spinning" small></b-spinner>
            </a>
            <div v-else-if="isLoading">
                <b-spinner class="mr-2" label="Spinning"></b-spinner>
                <strong>Loading equivalent drugs...</strong>
            </div>
        </div>

        <!-- Table Mode -->
        <div v-else-if="mode === 'table'">
            <div v-if="isLoading">
                <b-spinner class="mr-2" label="Spinning"></b-spinner>
                <strong>Loading equivalent drugs...</strong>
            </div>
            <div v-else-if="equivalentDrugs && equivalentDrugs.length" id="scrollspy-example">
                <b-row>
                    <b-col>
                        <b-table id="equivalent-drugs-table" ref="equivalent-drugs-table" :fields="equivalentDrugFields"
                                 :items="equivalentDrugs" :select-mode="'single'" outlined responsive="true" selectable
                                 sort-icon-left striped @row-selected="equivalentDrugSelected"></b-table>
                    </b-col>
                </b-row>
            </div>
        </div>

        <div v-if="equivalentDrugs.length===0 && searchWasClicked && !isLoading">
            <b>No equivalent drugs found.</b>
        </div>
    </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Drug } from '@/models/Drug/Drug';
import { CompoundDrug } from '@/models/Drug/CompoundDrug';
import { DrugProduct, DrugSource } from '@/models/Drug/DrugProduct';
import axios from 'axios';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import TextUtils from '@/util/TextUtils';

@Component({
    name: "EquivalentDrugsComponent",
    components: {}
})
export default class EquivalentDrugsComponent extends Vue {

    @Prop({ type: Object, default: new Drug() }) private drug!: DrugProduct;
    @Prop({ type: Boolean, default: false }) private disabled!: boolean;
    @Prop({ type: Boolean, default: true }) private onlyActive!: boolean;
    @Prop({ type: Boolean, default: false }) private prepopulate!: boolean;
    @Prop({ type: Boolean, default: true }) private showSearchButton!: boolean;
    @Prop({ type: String, default: 'search' }) private mode!: 'search' | 'table';
    @Prop({ default: undefined }) private tabindex?: number;

    private isLoading = false;
    protected equivalentDrugs: Array<Drug.EquivalentDrug> = [];
    private searchWasClicked = false;
    private equivalentDrugFields = [
        { key: 'onHand', label: 'Available', sortable: true },
        { key: 'ndc', label: 'NDC' },
        { key: 'brandGenericStatus', label: 'Brand/Generic' },
        { key: 'productNameLong', label: 'Name', sortable: true },
        { key: 'marketer', sortable: true },
        { key: 'packageSize', sortable: true },
        { key: 'lastPurchasePrice', label: 'Last Purchase', sortable: true,
          formatter: (value: number | null) => value != null ? TextUtils.currencyFormatter(value) : '-' },
        { key: 'averageCost90Days', label: 'Avg Cost (90d)', sortable: true,
          formatter: (value: number | null) => value != null ? TextUtils.currencyFormatter(value) : '-' },
    ];

    @Watch('drug.productID', { immediate: true })
    drugChanged() {
        if (this.prepopulate) this.findEquivalentDrugs();
    }

    @Watch('onlyActive')
    public findEquivalentDrugs() {
        if (!this.drug) {
            return;
        }

        // Check if it's a compound drug (source === 2)
        const isCompound = this.drug.source === DrugSource.CompoundProduct || this.drug.source === 2;

        if (isCompound) {
            // For compound drugs, get the compound ID
            const compoundId = (this.drug as any).compoundID || this.drug.drugId || this.drug.packageID;

            if (!compoundId) {
                return;
            }

            this.findEquivalentCompounds();
        } else {
            // For GSDD drugs, use the existing logic
            if (!this.drug.productID || !this.drug.packageID) {
                return;
            }
            this.findEquivalentGsddDrugs();
        }
    }

    private findEquivalentGsddDrugs() {
        this.searchWasClicked = true;
        this.isLoading = true;
        axios.get<Array<Drug.EquivalentDrug>>(`/Drug/${this.drug.productID}/Equivalent/${this.drug.packageID}`, { params: { onlyActive: this.onlyActive } })
            .then(response => {
                this.equivalentDrugs = response.data.filter(d => d.packageID != this.drug.packageID);
                this.$emit('drugsSelected', this.equivalentDrugs);
            })
            .catch(error => {
                console.error('Error while searching for equivalent drugs.', { error, response: error?.response });
                this.$notification(NotificationOptions.error(error));
            })
            .finally(() => {
                this.isLoading = false;
            });
    }

    private findEquivalentCompounds() {
        // Get the compound ID - try compoundID first, then fall back to drugId or packageID
        const compoundId = (this.drug as any).compoundID || this.drug.drugId || this.drug.packageID;

        if (!compoundId) {
            console.error('No compound ID available');
            return;
        }

        this.searchWasClicked = true;
        this.isLoading = true;

        interface EquivalentCompound {
            compoundID: number;
            ndc: string;
            name: string;
            labelName: string;
            status: number;
            equivalencyDepth: number;
        }

        axios.get<EquivalentCompound[]>(`/Compound/${compoundId}/equivalents`)
            .then(response => {
                // Transform compound equivalents to match EquivalentDrug structure
                this.equivalentDrugs = response.data.map(compound => ({
                    packageID: compound.compoundID,
                    ndc: compound.ndc || '',
                    productNameLong: compound.name || '',
                    brandGenericStatus: compound.equivalencyDepth === 1 ? 'Direct' : `${compound.equivalencyDepth} hops`,
                    marketer: '',
                    packageSize: '',
                    onHand: 0,
                    source: DrugSource.CompoundProduct, // Add source so parent knows it's a compound drug
                } as any));

                this.$emit('drugsSelected', this.equivalentDrugs);
            })
            .catch(error => {
                console.error('Error while searching for equivalent compounds.', { error, response: error?.response });
                this.$notification(NotificationOptions.error(error));
            })
            .finally(() => {
                this.isLoading = false;
            });
    }

    equivalentDrugSelected(items: Array<Drug.EquivalentDrug>) {
        if (!items.length) return;
        this.$emit('drugSelected', items[0].packageID);
    }

}
</script>

<style scoped>
#scrollspy-example {
    position: relative;
    height: 200px;
    overflow-y: scroll;
}
</style>
